import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Grid, Paper, alpha, useMediaQuery, useTheme } from '@mui/material';

import { parseJsonObject } from '~/utils/societePayloadBuilder/parseHelpers';

import { usePayloadBuilderToolbarActions } from './PayloadBuilderToolbarActions';

const WORKSPACE_MAX_HEIGHT = 'calc(100vh - 300px)';

interface StandalonePayloadBuilderPageProps<TState> {
  onProgressChange?: (hasProgress: boolean) => void;
  createDefaultState: () => TState;
  hasProgress: (state: TState, loadJson: string) => boolean;
  parsePayload: (json: unknown) => TState;
  validate: (state: TState) => { isValid: boolean };
  renderActions: (state: TState, isValid: boolean, fullWidth: boolean) => React.ReactNode;
  renderForm: (props: {
    collapseResetKey: number;
    state: TState;
    onChange: (next: TState) => void;
    loadJson: string;
    onLoadJsonChange: (value: string) => void;
    loadError: string | null;
    loadCollapseKey: number;
    onLoadPayload: () => void;
    onClearLoad: () => void;
    onUploadFile: (file: File) => void;
  }) => React.ReactNode;
  renderPreview: (state: TState, borderSoft: string, maxHeight: string) => React.ReactNode;
}

export default function StandalonePayloadBuilderPage<TState>({
  onProgressChange,
  createDefaultState,
  hasProgress,
  parsePayload,
  validate,
  renderActions,
  renderForm,
  renderPreview,
}: StandalonePayloadBuilderPageProps<TState>) {
  const theme = useTheme();
  const borderSoft = alpha(theme.palette.divider, 0.12);
  const isMobileToolbar = useMediaQuery(theme.breakpoints.down('md'));

  const [state, setState] = useState<TState>(() => createDefaultState());
  const [loadJson, setLoadJson] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [collapseResetKey, setCollapseResetKey] = useState(0);
  const [loadCollapseKey, setLoadCollapseKey] = useState(0);

  const validation = useMemo(() => validate(state), [state, validate]);
  const toolbarActions = useMemo(
    () => renderActions(state, validation.isValid, isMobileToolbar),
    [isMobileToolbar, renderActions, state, validation.isValid],
  );

  usePayloadBuilderToolbarActions(toolbarActions);

  useEffect(() => {
    onProgressChange?.(hasProgress(state, loadJson));
  }, [hasProgress, loadJson, onProgressChange, state]);

  const applyJson = useCallback(
    (text: string) => {
      try {
        setState(parsePayload(parseJsonObject(text)));
        setCollapseResetKey((value) => value + 1);
        setLoadCollapseKey((value) => value + 1);
        setLoadError(null);
      } catch (error) {
        setLoadError(error instanceof Error ? error.message : 'Invalid JSON');
      }
    },
    [parsePayload],
  );

  const handleLoadPayload = useCallback(() => {
    applyJson(loadJson);
  }, [applyJson, loadJson]);

  const handleUploadFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const text = typeof reader.result === 'string' ? reader.result : '';
        setLoadJson(text);
        applyJson(text);
      };
      reader.readAsText(file);
    },
    [applyJson],
  );

  const panelSx = {
    borderRadius: 2,
    border: `1px solid ${borderSoft}`,
    maxHeight: WORKSPACE_MAX_HEIGHT,
    overflow: 'auto',
    boxSizing: 'border-box',
  } as const;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5.4 }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 1.75 }}>
            {renderForm({
              collapseResetKey,
              state,
              onChange: setState,
              loadJson,
              onLoadJsonChange: setLoadJson,
              loadError,
              loadCollapseKey,
              onLoadPayload: handleLoadPayload,
              onClearLoad: () => {
                setLoadJson('');
                setLoadError(null);
              },
              onUploadFile: handleUploadFile,
            })}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6.6 }}>{renderPreview(state, borderSoft, WORKSPACE_MAX_HEIGHT)}</Grid>
      </Grid>
    </Box>
  );
}
